import { Alert, Flex, Tag, Typography } from 'antd'
import { WalletOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'
import { AmountCard } from './AmountCard'
import type { PartnerState } from './profileApi'
import type { MyAccount } from './useMyAccount'

const STATE_COLOR: Record<PartnerState, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  CLOSED: 'red',
}

/**
 * Sherikning hisobi — REJIMGA QARAB BOSHQA EKRAN.
 *
 * <h2>⚠️ Ikki model bir ekranning yashiringan qismlari EMAS</h2>
 * `SettlementMode` javadoc'i buni aniq yozadi: «ikki model
 * aralashtirilmaydi». Shuning uchun bu yerda ham `DEPOSIT` va
 * `POSTPAID` uchun boshqa-boshqa tartib chiziladi va ularning ASOSIY
 * RAQAMI ham boshqa maydon:
 *
 *   · `DEPOSIT`  — `available` (qoldiq + qarz limiti). `DepositService`
 *     javadoc'i aynan shuni talab qiladi: «Panel aynan shu raqamni
 *     ko'rsatishi kerak — "balans" emas», aks holda balansi nol, lekin
 *     limiti bor sherik o'zini ishlay olmaydigan deb o'ylaydi.
 *   · `POSTPAID` — `outstanding` (kun oxirida topshiriladigan pul). Bu
 *     modelda sherik oldindan to'lamaydi, ya'ni «qancha bera olaman»
 *     emas, «qancha qarzdorman» degan savol birinchi.
 *
 * <h2>⚠️ «Topshirilmagan naqd» bloki DEPOSIT da CHIZILMAYDI</h2>
 * O'sha rejimda `cash_in_transit` umuman ishlatilmaydi (§7.1): fuqarodan
 * olingan naqd sherikniki, chunki u bizga allaqachon to'lagan. Nol
 * ko'rsatish «topshirishim kerakmi?» degan YO'Q savolni tug'dirardi.
 *
 * <h2>⚠️ «Naqd topshirish» TUGMASI YO'Q</h2>
 * Egasi qarori (§10-1): `handOverCash` — XODIM ishi. Endpoint
 * `payment.create` huquqi ostida va u sherik to'plamida yo'q, ya'ni
 * tugma qo'yilsa u har bosishda 403 berardi.
 */
export function AccountPanel({ account }: { account: MyAccount }) {
  const { t } = useTranslation()

  if (account.agentId === null) {
    return (
      <EmptyState
        icon={<WalletOutlined />}
        title={t('profile.noNodeTitle')}
        description={t('profile.noNodeText')}
      />
    )
  }

  if (account.error && account.balance === null) {
    return <LoadError titleKey="profile.loadFailed" error={account.error} />
  }

  const node = account.node
  const balance = account.balance
  const loading = account.loading
  const mode = node?.settlementMode ?? null

  /**
   * ⚠️ «Qo'yilmagan» limitning MATNI REJIMGA BOG'LIQ va bu majburiy
   * (`DepositService.debtLimit` javadoc'i): `DEPOSIT` da bo'sh limit
   * «minusga ruxsat yo'q», `POSTPAID` da esa «cheklov yo'q» degani.
   * Ikkalasiga `0` yozish ikki xil holatni bitta yolg'onga aylantirardi.
   */
  const debtLimitText =
    balance === null
      ? t('common.dash')
      : balance.debtLimit !== null
        ? formatMoneyValue(balance.debtLimit)
        : mode === 'POSTPAID'
          ? t('profile.debtLimitNone')
          : t('profile.debtLimitUnset')

  const depositCard = (
    <AmountCard
      label={t('profile.depositBalance')}
      value={formatMoneyValue(balance?.balance)}
      tone={balance && balance.balance.amount < 0 ? 'danger' : 'default'}
      loading={loading}
    />
  )
  const limitCard = (
    <AmountCard label={t('profile.debtLimit')} value={debtLimitText} loading={loading} />
  )

  return (
    <Flex vertical gap={12}>
      <Flex align="center" gap={8} wrap>
        <Typography.Text strong style={{ fontSize: 18 }}>
          {node?.displayName ?? t('common.dash')}
        </Typography.Text>
        {node ? <Tag>{node.code}</Tag> : null}
        {node ? (
          <Tag color={STATE_COLOR[node.state]}>{t(`partnerState.${node.state}`)}</Tag>
        ) : null}
      </Flex>

      {/* ⚠️ Sabab OLDINDAN aytiladi: server bunday sherikning sotuvini
          baribir rad etadi va ogohlantirish bitta bekor urinishni
          tejaydi (§7 dagi oxirgi qator). */}
      {node && node.state !== 'ACTIVE' ? (
        <Alert type="warning" showIcon message={t('profile.suspended')} />
      ) : null}

      {mode === null ? (
        <Alert type="warning" showIcon message={t('profile.modeUnknown')} />
      ) : null}

      {mode === 'POSTPAID' ? (
        <>
          <AmountCard
            primary
            label={t('profile.outstanding')}
            value={formatMoneyValue(account.outstanding)}
            hint={t('profile.outstandingHint')}
            tone={account.outstanding && account.outstanding.amount > 0 ? 'warning' : 'default'}
            loading={loading}
          />
          {account.outstandingError ? (
            <LoadError titleKey="profile.outstandingFailed" error={account.outstandingError} />
          ) : null}
          <Flex gap={10} wrap>
            {depositCard}
            <AmountCard
              label={t('profile.available')}
              value={formatMoneyValue(balance?.available)}
              loading={loading}
            />
            {limitCard}
          </Flex>
        </>
      ) : (
        <>
          <AmountCard
            primary
            label={t('profile.available')}
            value={formatMoneyValue(balance?.available)}
            hint={t('profile.availableHint')}
            tone={balance && balance.available.amount <= 0 ? 'danger' : 'default'}
            loading={loading}
          />
          <Flex gap={10} wrap>
            {depositCard}
            {limitCard}
          </Flex>
        </>
      )}
    </Flex>
  )
}
